import { styled } from '@mui/material/styles';
import React, { ChangeEvent, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import ArrowDownSFillIcon from 'remixicon-react/ArrowDownSFillIcon';
import { FilterType, $FormControlLabel } from './Filter';
import CheckboxOption from './CheckboxOption';

type FilterOptionsProps = {
  filter: FilterType;
  onCheckboxChange: (
    filter: FilterType,
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void;
  onRadioChange: (filter: FilterType, event: ChangeEvent<HTMLInputElement>) => void;
};

function FilterOptions({ filter, onCheckboxChange, onRadioChange }: FilterOptionsProps) {
  const options = useMemo(() => {
    const blankOptionIndex = filter.options.findIndex((option) => option.value === '(blank)');
    if (blankOptionIndex > -1) {
      const updatedOptions = [...filter.options];
      updatedOptions.splice(blankOptionIndex, 1);
      return [...updatedOptions, filter.options[blankOptionIndex]];
    }
    return filter.options;
  }, [filter.options]);

  return (
    <$Accordion defaultExpanded={filter.defaultOpen}>
      <AccordionSummary
        aria-controls="panel1d-content"
        id="panel1d-header"
        expandIcon={<ArrowDownSFillIcon />}
        sx={{ px: 1 }}
      >
        <Box display="flex" alignItems="center">
          <Typography fontWeight="bold">{filter?.displayName || filter.category}</Typography>
        </Box>
      </AccordionSummary>
      <$AccordionDetails
        sx={{ height: options.length > 100 ? 300 : 'fit-content', maxHeight: 400 }}
      >
        {filter.inputType !== 'radio' && filter.showSelectAll && (
          <Box>
            <$FormControlLabel
              control={
                <Checkbox
                  onChange={(...params) => onCheckboxChange(filter, ...params)}
                  checked={filter?.allOptions?.length === filter?.selectedOptions?.length}
                />
              }
              label="Select All"
              value="Select All"
              className="select-all"
            />
          </Box>
        )}
        {filter.inputType !== 'radio' &&
          (options.length > 100 ? (
            <Virtuoso
              style={{
                height: '100%',
                overflowX: 'hidden',
                overflowY: 'auto',
              }}
              data={options}
              itemContent={(_index, option) => (
                <CheckboxOption
                  key={`${_index}${option.label}`}
                  filter={filter}
                  option={option}
                  onCheckboxChange={onCheckboxChange}
                />
              )}
            />
          ) : (
            <>
              {options.map((option, index) => (
                <CheckboxOption
                  key={`${index}${option.label}`}
                  filter={filter}
                  option={option}
                  onCheckboxChange={onCheckboxChange}
                />
              ))}
            </>
          ))}
        {filter.inputType === 'radio' && (
          <RadioGroup
            value={filter.selectedValue}
            onChange={(event) => onRadioChange(filter, event)}
            defaultValue={filter.selectedValue}
            name="selectedValue"
          >
            {options.map((option, index) => (
              <Box key={`${index}${option.label}`}>
                <$FormControlLabel
                  value={option.value}
                  control={<Radio size="small" />}
                  label={
                    <Box component="span" width="100%" display="flex" alignItems="center">
                      {option.label}&nbsp;
                      {option.badgeValue ? `(${option.badgeValue})` : ''}
                    </Box>
                  }
                />
              </Box>
            ))}
          </RadioGroup>
        )}
      </$AccordionDetails>
    </$Accordion>
  );
}

const $Accordion = styled(Accordion, { label: 'Accordion' })({
  margin: '8px 0',
  boxShadow: 'none',
  backgroundColor: 'transparent',
  '&::before': {
    display: 'none',
  },
});

const $AccordionDetails = styled(AccordionDetails, { label: 'AccordionDetails' })({
  overflowX: 'hidden',
  overflowY: 'auto',
  padding: '0 8px',
});

export default FilterOptions;
